import { BindingEngine, observable } from 'aurelia-binding';
import { BalanceReportViewModel, BalanceTotalsQueryModel, ClickHouseReportDateRange, ClickHouseReportDateRangeInterval, GlReportsClient, PortfolioReportClient } from '@dynamik/common/clients/reporting-clients';
import { CacheService, CustomerService, IApplicationFilter, NumericFormatterValueConverter, property, widget } from '@dynamik/common';
import { WidgetBase } from '@dynamik/common';
import { customElement } from 'aurelia-templating';
import { ColumnState, GridApi, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { ReplaySubject } from '../../../rxjs-local';
import { ThemeService } from '../../../services/theme-service';
import { Size } from '@aurelia-mdc-web/base';
import { addDays, format, startOfYear } from '../../../date-fns-local';

interface IBalanceSheetRow {
  name: string;
  prev: number;
  end: number;
  isTotal?: boolean;
}

@customElement('balance-sheet-widget')
@widget('Balance sheet', false, true, 'Balance Sheet description')
export class BalanceSheetWidget extends WidgetBase {
  constructor(element: Element, customerService: CustomerService, private glReportsClient: GlReportsClient, bindingEngine: BindingEngine, private cacheService: CacheService,
    private numericFormatter: NumericFormatterValueConverter, public themeService: ThemeService, private portfolioReportClient: PortfolioReportClient) {
    super(element, bindingEngine, customerService);
    this.gridReady$ = new ReplaySubject<void>(1);
  }

  gridApi: GridApi<IBalanceSheetRow>;
  suppressSaveColumnState: boolean;
  gridOptions: GridOptions<IBalanceSheetRow>;
  data: BalanceReportViewModel;
  gridReady$ = new ReplaySubject<void>(1);
  moneyFormatter = (params: ValueFormatterParams) => this.numericFormatter.toView(params.value as number, 0);

  @property(null)
  columnStates?: ColumnState[];

  @property(null)
  filterModel?: ReturnType<GridApi['getFilterModel']>;

  @property('Table')
  fitColumns: boolean;

  @property('Table')
  fontSize?: number = 8;

  @property('Table')
  rowHeight?: number = 20;

  @observable
  size: Size;
  sizeChanged() {
    if (this.gridApi && this.fitColumns) {
      this.gridApi.sizeColumnsToFit();
    }
  }

  override activate(config: unknown) {
    super.activate(config)
    this.gridOptions = {
      defaultColDef: {
        sortable: false,
        resizable: true,
        cellStyle: x => ({
          'border-top': x.data.isTotal ? `1px solid ${this.themeService.getChartLabelColour(undefined)}` : 'none',
          'border-bottom': x.data.isTotal ? `1px solid ${this.themeService.getChartLabelColour(undefined)}` : 'none'
        }),
      },
      popupParent: document.body,
      rowHeight: this.rowHeight,
      rowModelType: 'clientSide',
      rowSelection: { mode: 'singleRow', checkboxes: false },
      animateRows: false,
      scrollbarWidth: 11,
      onColumnMoved: () => this.saveColumnState(),
      onColumnVisible: () => this.saveColumnState(),
      onColumnResized: () => this.saveColumnState(),
      onFilterChanged: () => this.saveColumnState(),
      onSortChanged: () => this.saveColumnState(),
      onDisplayedColumnsChanged: x => {
        if (x.source !== 'gridInitializing') {
          this.saveColumnState();
        }
      },
      getRowId: x => x.data.name,
      onGridReady: params => {
        this.gridApi = params.api;
        this.gridReady$.next();
      },
      onFirstDataRendered: () => {
        this.suppressSaveColumnState = true;
        setTimeout(() => this.suppressSaveColumnState = false, 2000);
        if (this.fitColumns) {
          this.gridApi.sizeColumnsToFit();
        } else {
          if (this.columnStates?.length) {
            this.gridApi.applyColumnState({ state: this.columnStates, applyOrder: true });
          }
        }
        if (this.filterModel?.length) {
          this.gridApi.setFilterModel(this.filterModel);
        }
      }
    };
  }

  saveColumnState() {
    if (this.suppressSaveColumnState || !this.gridApi) {
      return;
    }
    this.columnStates = this.gridApi.getColumnState();
    this.filterModel = this.gridApi.getFilterModel();
    this.element.dispatchEvent(new CustomEvent('widget-change', { bubbles: true }));
  }

  protected override async loadData(filter: IApplicationFilter): Promise<BalanceTotalsQueryModel[]> {
    const prevYearEndStr = format(addDays(startOfYear(filter.dates.to), -1), 'yyyy-MM-dd');
    const prevPrevYearEnd = format(addDays(startOfYear(prevYearEndStr), -1), 'yyyy-MM-dd');
    const test = await this.glReportsClient.balance(filter.customer.companyId, filter.dates.toStr);
    return await this.portfolioReportClient.balance(filter.customer.companyId, [
      new ClickHouseReportDateRange({ name: "prevPrevYearEnd", from: prevPrevYearEnd, to: prevPrevYearEnd, interval: ClickHouseReportDateRangeInterval.Day }),
      new ClickHouseReportDateRange({ name: "prevYearEnd", from: prevYearEndStr, to: prevYearEndStr, interval: ClickHouseReportDateRangeInterval.Day }),
      new ClickHouseReportDateRange({ name: "end", from: filter.dates.toStr, to: filter.dates.toStr, interval: ClickHouseReportDateRangeInterval.Day })
    ]);
  }

  protected override async setData(data: BalanceTotalsQueryModel[]): Promise<void> {
    const ppe = data.find(x => x.periodName === 'prevPrevYearEnd');
    const pe = data.find(x => x.periodName === 'prevYearEnd');
    const e = data.find(x => x.periodName === 'end');

    const changeInValue = e.unrealizedProfitLoss - pe.unrealizedProfitLoss;
    const eResult = e.glNotChangeInValue - pe.glNotChangeInValue + changeInValue;
    const eRetainedEarnings = e.glRetainedEarnings + pe.unrealizedProfitLoss + pe.glEverything - ppe.glEverything - pe.glMarketAdjustment;
    const pTotalEquity = pe.glPaidEquity + pe.glRetainedEarnings;
    const eTotalEquity = e.glPaidEquity + eRetainedEarnings + eResult;
    const pTotalLiabilities = pe.glLiabilities + pe.glLiabilitiesGroupCompanies;
    const eTotalLiabilities = e.glLiabilities + e.glLiabilitiesGroupCompanies;

    const pOperatingIncome = pe.glRevenues + pe.glCostOfGoodsSold;
    const pOperatingProfit = pOperatingIncome + pe.glPersonellExpenses + pe.glOtherOperatingExpenses;
    const pNetFinancialItems = pe.glFinancialIncomeAndExpenses + pe.unrealizedProfitLoss;
    const pNetIncome = pOperatingProfit + pNetFinancialItems;

    const eOperatingIncome = e.glRevenues + e.glCostOfGoodsSold;
    const eOperatingProfit = eOperatingIncome + e.glPersonellExpenses + e.glOtherOperatingExpenses;
    const eNetFinancialItems = e.glFinancialIncomeAndExpenses + e.unrealizedProfitLoss;
    const eNetIncome = eOperatingProfit + eNetFinancialItems;


    const rowData = [
      { name: 'Intangible assets', prev: pe.glIntangibleAssets, end: e.glIntangibleAssets },
      { name: 'Tangible assets', prev: pe.glTangibleAssets, end: e.glTangibleAssets },
      { name: 'Receivables', prev: pe.glReceivables, end: e.glIntangibleAssets },
      { name: 'Receivables group companies', prev: pe.glReceivablesGroupCompanies, end: e.glReceivablesGroupCompanies },
      { name: 'Investments (base invested)', prev: pe.glInvestmentsBaseInvested, end: e.glInvestmentsBaseInvested },
      { name: 'Investments (acc performance)', prev: pe.unrealizedProfitLoss, end: e.unrealizedProfitLoss },
      { name: 'Deposits', prev: pe.glDeposits, end: e.glDeposits },
      {
        name: 'Total assets',
        prev: pe.glIntangibleAssets + pe.glTangibleAssets + pe.glReceivables + pe.glReceivablesGroupCompanies + pe.glInvestmentsBaseInvested + pe.unrealizedProfitLoss + pe.glDeposits,
        end: e.glIntangibleAssets + e.glTangibleAssets + e.glReceivables + e.glReceivablesGroupCompanies + e.glInvestmentsBaseInvested + e.unrealizedProfitLoss + e.glDeposits,
        isTotal: true
      },
      { name: 'Paid equity', prev: pe.glPaidEquity, end: e.glPaidEquity },
      { name: 'Retained earnings', prev: null, end: eRetainedEarnings },
      { name: 'YTD result', prev: null, end: eResult },
      { name: 'Total equity', prev: pTotalEquity, end: eTotalEquity },
      { name: 'Liabilities', prev: pe.glLiabilities, end: e.glLiabilities },
      { name: 'Liabilities group companies', prev: pe.glLiabilitiesGroupCompanies, end: e.glLiabilitiesGroupCompanies },
      { name: 'Total liabilities', prev: pTotalLiabilities, end: eTotalLiabilities },
      { name: 'Total equity and liabilities', prev: pTotalEquity + pTotalLiabilities, end: eTotalEquity + eTotalLiabilities, isTotal: true },
    ];
    this.gridApi.setGridOption('rowData', rowData);
    this.excelData = {
      fileName: 'Balance Sheet',
      data: { Items: rowData }
    };
    this.gridApi.getColumn('prev').getColDef().headerName = format(pe.date, 'dd.MM.yyyy');
    this.gridApi.getColumn('end').getColDef().headerName = format(e.date, 'dd.MM.yyyy');
    this.gridApi.refreshHeader();
  }

  override configChanged(key: keyof BalanceSheetWidget) {
    switch (key) {
      case 'fontSize':
        break;
      case 'rowHeight':
        this.rowHeightChanged();
        break;
      default:
        this.refresh$.next(undefined);
        break;
    }
  }

  rowHeightChanged() {
    this.gridApi.setGridOption('rowHeight', this.rowHeight);
    this.gridApi.onRowHeightChanged();
  }
}
